import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { paths } from 'src/config/paths'
import { SendInvitePopup } from 'src/features/invites/components/send-invite-popup'
import { AiLightning } from 'src/shared/components/icons/ai-lightning'
import { Invite } from 'src/shared/components/icons/invite'
import { SubscriptionLogo } from 'src/shared/components/icons/logos/subscription'
import { Button } from 'src/shared/components/ui/buttons/button'
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader } from 'src/shared/components/ui/miscellaneous/sidebar'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import { UserCard } from 'src/shared/features/users/components/cards/user-card'
import type { PrivateUserData } from 'src/shared/features/users/schemas/user'
import { VisibilityCard } from 'src/shared/features/visibility/components/card/scorebar'

import { SidebarMenuItemsBottom, SidebarMenuItemsTop } from './nav-config'
import { NavMain } from './nav-main'

// Memoized user card component
const MemoizedUserCard = React.memo(({ user }: { user: PrivateUserData }) => {
  const navigate = useNavigate()
  const handleNavigate = React.useCallback(() => {
    navigate(paths.users.profile.getHref(user.username))
  }, [navigate, user?.username])

  return (
    <div className="relative z-50 mx-4">
      <UserCard
        userId={user.id}
        buttonType="none"
        className="w-full"
        isMe={true}
        showUsername={false}
        showAddress={false}
        clickable={true}
        onClick={handleNavigate}
      />
    </div>
  )
})

MemoizedUserCard.displayName = 'MemoizedUserCard'

// Memoize the footer content to prevent unnecessary rerenders
const SidebarFooterContent = React.memo(({ user }: { user: PrivateUserData }) => {
  return (
    <div className="relative flex flex-col gap-2">
      <VisibilityCard />
      {user && <MemoizedUserCard user={user} />}
      <NavMain items={SidebarMenuItemsBottom} />
    </div>
  )
})

SidebarFooterContent.displayName = 'SidebarFooterContent'

// Memoize the header content
const SidebarHeaderContent = React.memo(() => {
  return <SubscriptionLogo />
})

SidebarHeaderContent.displayName = 'SidebarHeaderContent'

// Memoize the main content
const SidebarMainContent = React.memo(() => {
  const navigate = useNavigate()
  const [inviteOpen, setInviteOpen] = React.useState(false)

  return (
    <div className="space-y-3">
      <NavMain items={SidebarMenuItemsTop} />
      <Button
        className="mx-4 flex w-[calc(100%-2rem)] items-center justify-center rounded-3xl"
        onClick={() => navigate(paths.home.getHref())}
      >
        Match / Refer
      </Button>
      <SendInvitePopup
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        trigger={
          <Button
            variant="secondaryInverse"
            className="mx-4 flex w-[calc(100%-2rem)] items-center justify-center gap-2 rounded-full"
          >
            <Invite className="size-6" />
            Invite
          </Button>
        }
      />
      <Button
        variant="outline"
        leftIcon={<AiLightning className="size-4" />}
        className="mx-4 flex w-[calc(100%-2rem)] items-center justify-center rounded-3xl border-secondary"
        onClick={() => navigate(paths.assistant.path)}
      >
        Chat with Lynk
      </Button>
    </div>
  )
})

SidebarMainContent.displayName = 'SidebarMainContent'

export const AppSidebar = React.memo(({ ...props }: React.ComponentProps<typeof Sidebar>) => {
  const { data: user, isLoading: isUserLoading } = useMyUserData()

  return (
    <Sidebar {...props}>
      <SidebarHeader className="mb-2">
        <SidebarHeaderContent />
      </SidebarHeader>

      <SidebarContent>
        <SidebarMainContent />
      </SidebarContent>

      {!isUserLoading && <SidebarFooter>{user && <SidebarFooterContent user={user} />}</SidebarFooter>}
    </Sidebar>
  )
})

AppSidebar.displayName = 'AppSidebar'
